import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { ErreurSummaryConfiguration } from '@/features/application/erreur-summary'
import { CXPI_ACTIONS } from '@/features/interface/demande/cxpi/useDemandeCXPIActions'
import { useDemandeCXPIActions } from '@/features/interface/demande/cxpi/useDemandeCXPIActions.ts'
import type { SetupDemande } from '@/features/interface/demande/shared/components/DemandeContent/DemandeContent.types.ts'
import { type Component, provide } from 'vue'

import {
  CXPI_INTERRUPTIONS_ACTIONS,
  useInterruptionsActions,
} from '@/features/interface/demande/cxpi/steps/contrat/useInterruptionsActions.ts'
import {
  CXPI_MODULES_ACTIONS,
  useModuleActions,
} from '@/features/interface/demande/cxpi/steps/formation/useModuleActions.ts'
import { useFormationDemandeCXPIStore } from '@/features/interface/demande/cxpi/useCXPIStore.ts'
import ContratStep from './steps/contrat/ContratStep.vue'
import DocumentsStep from './steps/documents/DocumentsStep.vue'
import DRFStep from './steps/drf/DRFStep.vue'
import EmployeurStep from './steps/employeur/EmployeurStep.vue'
import FinancementStep from './steps/financement/FinancementStep.vue'
import FormationStep from './steps/formation/FormationStep.vue'
import IntroductionStep from './steps/introduction/IntroductionStep.vue'
import SalarieStep from './steps/salarie/SalarieStep.vue'
import SyntheseStep from './steps/synthese/SyntheseStep.vue'
import { CXPI_DOC_REFRESH, useDocumentsRefreshCXPI } from '@/features/interface/demande/cxpi/useDocumentsRefreshCXPI.ts'

type TabKey =
  | 'DRF'
  | 'Introduction'
  | 'Employeur'
  | 'Salarié'
  | 'Contrat'
  | 'Formation'
  | 'Financement'
  | 'Synthèse'
  | 'Documents'

const TABS_VUES: Record<TabKey, Component> = {
  DRF: DRFStep,
  Introduction: IntroductionStep,
  Employeur: EmployeurStep,
  Salarié: SalarieStep,
  Contrat: ContratStep,
  Formation: FormationStep,
  Financement: FinancementStep,
  Synthèse: SyntheseStep,
  Documents: DocumentsStep,
} as const

const TABS_ERR_CONFIG: ErreurSummaryConfiguration<ContratCxpiDTO> = {
  DRF: { include: (state) => [state.drf?.dateFinValiditeDrf, state.drf?.statut] },
  Introduction: {
    include: (state) => [state.validationNotice],
  },
  Employeur: {
    includeDeep: (state) => [state.employeur],
  },
  Salarié: {
    includeDeep: (state) => [state.salarie],
  },
  Contrat: {
    includeDeep: (state) => [state.contrat, state.mission],
  },
  Formation: {
    includeDeep: (state) => [state.formation],
  },
  Financement: {
    includeDeep: (state) => [state.financement],
  },
  Synthèse: {
    include: (state) => [
      state.lieuSignature,
      state.commentaire,
      state.acceptationCg,
      state.attestationSurHonneur,
      state.dateSignature,
    ],
  },
  Documents: {
    includeDeep: (state) => [state.fichiers],
    include: (state) => [state.attestationPiecesJustificatives, state.drf?.courriers],
  },
} as const

export const SetupFormationDemandeCXPI: SetupDemande<'MODEL_CXPI', TabKey> = {
  useStore: useFormationDemandeCXPIStore,
  injectActions: () => {
    const store = useFormationDemandeCXPIStore()
    const actions = useDemandeCXPIActions(store)
    provide(CXPI_ACTIONS, actions)
    return actions
  },
  tabFirst: 'DRF',
  tabsVue: TABS_VUES,
  tabsErrorConfig: () => TABS_ERR_CONFIG,
  onInititalize: () => {
    const store = useFormationDemandeCXPIStore()
    provide(CXPI_MODULES_ACTIONS, useModuleActions(store))
    provide(CXPI_INTERRUPTIONS_ACTIONS, useInterruptionsActions(store))

    // useDocumentsRefreshCXPI utilise un Timeout qui doit être libéré
    const docRefresh = useDocumentsRefreshCXPI(store)
    provide(CXPI_DOC_REFRESH, docRefresh)
    return docRefresh.dispose
  },
} as const
